/* eslint-disable import/order */
import React from 'react';
import { AlertCircle, Pencil } from 'lucide-react';

import { PurchaseOrderActionsMenu } from 'components/procurement/components/purchaseOrdersV2/PurchaseOrderActionsMenu';
import { PurchaseOrderStatusCell } from 'components/procurement/components/purchaseOrdersV2/PurchaseOrderStatusCell';
import {
    formatPurchaseOrderDate,
    formatPurchaseOrderDateTime,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    isCrossDockPurchaseOrder,
    isPriorityPurchaseOrder,
    isPurchaseOrderConfirmed,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Button } from 'shadcn/components/ui/Button';

/**
 * Simple header content for the purchase order entity page.
 *
 * @param {Object} props Component props.
 * @param {boolean} [props.isEditing=false] Whether the PO is in edit mode.
 * @param {boolean} [props.hasValidationErrors=false] Whether active validation errors exist.
 * @param {boolean} [props.isSaving=false] Whether a save request is in progress.
 * @param {() => void} [props.onCancel] Callback for canceling edits.
 * @param {() => void} [props.onCancelPurchaseOrder] Callback for canceling the PO.
 * @param {() => void} [props.onDownloadCsv] Callback to download CSV.
 * @param {() => void} [props.onDownloadPdf] Callback to download PDF.
 * @param {() => void} [props.onDownloadShipList] Callback to download ship list.
 * @param {() => void} [props.onDuplicate] Callback to duplicate the PO.
 * @param {() => void} [props.onEdit] Callback to enter edit mode.
 * @param {() => void} [props.onSendToVendor] Callback to send to vendor.
 * @param {() => void} [props.onConfirm] Callback to confirm the PO.
 * @param {() => void} [props.onClearConfirmation] Callback to clear PO confirmation.
 * @param {boolean} [props.isConfirmationPending=false] Whether confirm/clear is in flight.
 * @param {() => void} [props.onSave] Callback to save edits.
 * @param {boolean} [props.isSendToVendorDisabled] Whether Send to Vendor is disabled.
 * @param {Object} [props.purchaseOrder] Purchase order data passed from the table view.
 * @param {string|number} [props.purchaseOrderId] Purchase order identifier from the route.
 * @returns {JSX.Element} Header content summarizing the purchase order.
 */
export function PurchaseOrderHeaderContent({
    hasValidationErrors = false,
    isConfirmationPending = false,
    isEditing = false,
    isSaving = false,
    isSendToVendorDisabled,
    onCancel,
    onCancelPurchaseOrder,
    onClearConfirmation,
    onConfirm,
    onDownloadCsv,
    onDownloadPdf,
    onDownloadShipList,
    onDuplicate,
    onEdit,
    onSave,
    onSendToVendor,
    purchaseOrder = null,
    purchaseOrderId,
}) {
    const isPriority = isPriorityPurchaseOrder(purchaseOrder);
    const isCrossDock = isCrossDockPurchaseOrder(purchaseOrder);
    const isConfirmed = isPurchaseOrderConfirmed(purchaseOrder);
    const createdAt = formatPurchaseOrderDate(purchaseOrder?.createdAt);
    const lastUpdatedAt = formatPurchaseOrderDateTime(purchaseOrder?.lastUpdatedAt ?? purchaseOrder?.createdAt);
    const lastSentAt = formatPurchaseOrderDateTime(purchaseOrder?.sentToVendor, '') || undefined;

    const isSaveDisabled = isSaving || hasValidationErrors;

    return (
        <div className="flex w-full flex-wrap items-start gap-3 lg:items-center">
            <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">PO# {purchaseOrderId}</h1>
                    <PurchaseOrderStatusCell
                        status={purchaseOrder?.status}
                        fallbackText="—"
                        isCrossDock={isCrossDock}
                        isPriority={isPriority}
                    />
                </div>
                <p className="text-sm text-muted-foreground">
                    Created On {createdAt} · Last Updated At {lastUpdatedAt}
                </p>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
                {isEditing ? (
                    <>
                        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="default"
                            size="sm"
                            onClick={onSave}
                            disabled={isSaveDisabled}
                            title={hasValidationErrors ? 'Fix validation errors to save' : undefined}
                        >
                            {isSaving ? (
                                'Saving…'
                            ) : (
                                <>
                                    {hasValidationErrors ? (
                                        <AlertCircle className="mr-2 h-4 w-4 text-destructive" aria-hidden="true" />
                                    ) : null}
                                    Save Changes
                                </>
                            )}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onEdit}
                            disabled={typeof onEdit !== 'function'}
                        >
                            <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                            Edit PO
                        </Button>
                        <PurchaseOrderActionsMenu
                            isConfirmed={isConfirmed}
                            isConfirmationPending={isConfirmationPending}
                            onCancel={onCancelPurchaseOrder}
                            onClearConfirmation={onClearConfirmation}
                            onConfirm={onConfirm}
                            onDuplicate={onDuplicate}
                            onDownloadCsv={onDownloadCsv}
                            onDownloadPdf={onDownloadPdf}
                            onDownloadShipList={onDownloadShipList}
                            onSendToVendor={onSendToVendor}
                            isSendToVendorDisabled={isSendToVendorDisabled}
                            lastSentAt={lastSentAt}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
